import type { GameResult, Turn } from '@strategy4kids/protocol';
import { expectiTurn } from './backgammon-ai.js';
import type { BackgammonCube, BackgammonCubeOffer } from './backgammon-cube.js';
import {
  type BackgammonDice,
  type BackgammonPosition,
  type BackgammonResult,
  type BackgammonTurn,
  applyTurn,
  backgammonResultOf,
  backgammonStartPosition,
  compareTurnsInMoverFrame,
  legalTurns,
  pipCount,
} from './backgammon.js';
import { IllegalMoveError } from './chess.js';

/**
 * The backgammon game-with-dice wrapper and a greedy robot.
 *
 * Backgammon cannot implement `GameAdapter`, and this is the module where the
 * reason is made concrete: a backgammon position is incomplete without the
 * current roll. Real backgammon interleaves hidden randomness (roll) with
 * open play (the dice play), and `MoveEngine`'s `setPosition(fen)` has no
 * place to put the dice — the same chess-shaped gap that kept the rules module
 * (`backgammon.ts`) out of `adapters.ts`. So this class speaks `BackgammonGame`
 * below and stays unregistered; the future app wiring decides how dice cross
 * the protocol boundary.
 *
 * The turn flow is explicit and non-negotiable in one place:
 * `roll → playTurn`, or `roll → (no legal turns) → playTurn` with an empty
 * turn, which is the pass. Everything else throws rather than guessing.
 */

export interface BackgammonGame {
  readonly position: BackgammonPosition;
  /** The current roll, or null before the roll (and always null once the
   *  game is over — a finished game takes no dice). */
  readonly dice: BackgammonDice | null;
  /** The doubling cube (additive, 2026-10-05). Optional so offline games and
   *  pre-cube snapshots need not carry it — read it through `cubeOf()`.
   *  Flow state like the dice, not checker geography: it lives on the game,
   *  never on `BackgammonPosition`. */
  readonly cube?: BackgammonCube;
  /** Present while a double awaits take/pass (additive, 2026-10-05). The
   *  doubler is still the side to move until the offer is answered. */
  readonly cubeOffer?: BackgammonCubeOffer | null;
}

/** `roll()`'s source. Injected so tests are deterministic; production gets
 *  `Math.random`. Must return [0, 1). */
export type DieRoller = () => number;

export interface BackgammonRobotOpts {
  /**
   * How loud the greed is. `'calm'` always takes the best turn by the
   * evaluation below; `'noisy'` shuffles among the top half — the difference
   * between a robot a seven-year-old can beat and one that never blunders.
   */
  readonly mood?: 'calm' | 'noisy';
  /** Rng for `'noisy'`; injected like `DieRoller` for determinism. */
  readonly rng?: DieRoller;
  /**
   * Search depth for the annealed robot (`expectiTurn`): 1 looks one
   * opponent exchange ahead, 2 adds another. When set, depth REPLACES the
   * mood — expectation over the opponent's dice subsumes greed-tuning.
   * Absent (or 0) keeps the greedy pick, bit-for-bit.
   */
  readonly depth?: 1 | 2;
}

/**
 * Turn evaluation from the mover's perspective: what the move does to the pip
 * race, plus a bonus for sending an opponent checker to the bar. Pips are the
 * honest signal — every step forward is a step the robot's kid-opponent can
 * see and learn from. Centipawn-style precision is not attempted; backgammon's
 * real evaluation is a research project, and a kid's robot does not need one.
 */
export function evaluateTurn(game: BackgammonGame, turn: BackgammonTurn): number {
  const color = game.position.turn;
  const enemy = color === 'w' ? 'b' : 'w';
  const before = pipCount(game.position, color) - pipCount(game.position, enemy);
  const after = applyTurn(game.position, turn);
  const raced = pipCount(after, color) - pipCount(after, enemy);
  // Improvement = how far the mover closed the pip differential: positive when
  // the gap shrank (or the opponent's stretch grew). The sign matters —
  // `chooseTurn` sorts descending, so an inverted sign crowns the *worst* move.
  const hitBonus = game.position.bar[enemy] < after.bar[enemy] ? 12 : 0;
  // Off-board checkers matter beyond their pips: each borne-off checker is a
  // point of the final score, so nudge the robot to take them when offered.
  const offBonus = (after.off[color] - game.position.off[color]) * 4;
  return before - raced + hitBonus + offBonus;
}

export class BackgammonAdapter {
  /** A plain string, deliberately not the protocol `GameId` — registering
   *  backgammon is an additive protocol change and a one-way door, deferred
   *  until the board/UI wiring exists. */
  readonly gameId = 'backgammon';

  private readonly rollDie: DieRoller;

  constructor(rollDie: DieRoller = Math.random) {
    this.rollDie = rollDie;
  }

  newGame(turn: Turn): BackgammonGame {
    return { position: backgammonStartPosition(turn), dice: null };
  }

  /** Resume from an arbitrary position (tests, future API persistence). */
  fromPosition(position: BackgammonPosition, dice: BackgammonDice | null = null): BackgammonGame {
    return { position, dice };
  }

  roll(game: BackgammonGame): BackgammonGame {
    if (backgammonResultOf(game.position)) return game; // a finished game takes no dice
    const die = () => 1 + Math.floor(this.rollDie() * 6);
    return { position: game.position, dice: [die(), die()] };
  }

  /** Empty when the roll cannot play — the caller passes with `{ steps: [] }`. */
  legalTurns(game: BackgammonGame): BackgammonTurn[] {
    if (!game.dice) throw new IllegalMoveError('backgammon: roll before playing');
    return legalTurns(game.position, game.dice);
  }

  playTurn(game: BackgammonGame, turn: BackgammonTurn): BackgammonGame {
    // Game-over outranks everything: a finished game is unplayable with or
    // without dice, and the message should say which state you're actually in.
    if (backgammonResultOf(game.position)) throw new IllegalMoveError('backgammon: game over');
    if (!game.dice) throw new IllegalMoveError('backgammon: roll before playing');
    if (turn.steps.length === 0) {
      // The pass is only honest when there was genuinely nothing to play.
      if (legalTurns(game.position, game.dice).length > 0) {
        throw new IllegalMoveError('backgammon: cannot pass a playable roll');
      }
      const enemy = game.position.turn === 'w' ? 'b' : 'w';
      return { position: { ...game.position, turn: enemy }, dice: null };
    }
    const legal = legalTurns(game.position, game.dice);
    const matches = legal.filter((t) => sameTurn(t, turn));
    if (matches.length === 0) throw new IllegalMoveError('backgammon: illegal dice play');
    return { position: applyTurn(game.position, turn), dice: null };
  }

  result(game: BackgammonGame): BackgammonResult | null {
    return backgammonResultOf(game.position);
  }

  /** The robot's pick among the current roll's legal turns. Throws on an
   *  unrolled or finished game, like `legalTurns`. */
  chooseTurn(game: BackgammonGame, opts: BackgammonRobotOpts = {}): BackgammonTurn {
    const legal = this.legalTurns(game);
    if (legal.length === 0) return { steps: [] };
    // The annealed tier: expectation over the opponent's dice beats greed
    // tuning, so depth replaces mood entirely when set.
    if (opts.depth) return expectiTurn(game, opts.depth);
    const scored = legal
      .map((turn) => ({ turn, score: evaluateTurn(game, turn) }))
      // Ties (most greedy scores tie) break in the mover's frame, not in
      // enumeration order — see compareTurnsInMoverFrame.
      .sort(
        (a, b) => b.score - a.score || compareTurnsInMoverFrame(game.position.turn, a.turn, b.turn),
      );
    const best = scored[0];
    if (!best) return { steps: [] };
    if (opts.mood === 'noisy') {
      const rng = opts.rng ?? Math.random;
      const top = scored.slice(0, Math.max(1, Math.ceil(scored.length / 2)));
      const pick = top[Math.floor(rng() * top.length)];
      if (pick) return pick.turn;
    }
    return best.turn;
  }
}

/** Sequence equality over steps — order matters, so a turn matches only when
 *  every step pairs up exactly. Keeps `playTurn` honest about which of the
 *  possibly-several equal-scoring legal plays was meant. */
function sameTurn(a: BackgammonTurn, b: BackgammonTurn): boolean {
  if (a.steps.length !== b.steps.length) return false;
  return a.steps.every((s, i) => {
    const o = b.steps[i];
    return o !== undefined && s.from === o.from && s.to === o.to && s.pips === o.pips;
  });
}

/**
 * The one place backgammon's result crosses into the protocol vocabulary.
 * Plain wins reuse `elimination`; `gammon`/`backgammon` are 1:1 union members
 * added for exactly this mapping. This function is the whole seam — the UI
 * reads `GameResult` and never imports `BackgammonResult`, so the local type
 * can change shape later without rippling.
 */
export function toGameResult(result: BackgammonResult): GameResult {
  switch (result.kind) {
    case 'elimination':
      return { kind: 'elimination', winner: result.winner };
    case 'gammon':
      return { kind: 'gammon', winner: result.winner };
    case 'backgammon':
      return { kind: 'backgammon', winner: result.winner };
  }
}
